import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { promangToast as toast } from "../../../../../operationFeedback/toastQueue";
import { normalizeError } from "../../../../../operationFeedback/normalizeError";
import { emptySjaHazard, nextSjaStepNumber, removeSjaRow, validateSjaForm } from "../../../../../components/dashboard/hse/risk/sjaHazards";
import { SjaHazardFields } from "../../../../../components/dashboard/hse/risk/SjaHazardFields";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { HsePageChrome } from "../../../../../components/dashboard/hse/HsePageChrome";
import { ResourceError } from "../../../../../components/utils/ResourceError";
import { ResourceLoading } from "../../../../../components/utils/ResourceLoading";
import { HseFieldInfoLabel } from "../../../../../components/dashboard/hse/HseFieldInfoLabel";
import { HsePageBody } from "../../../../../components/dashboard/hse/HsePageBody";
import { SjaStepTemplatePicker } from "../../../../../components/dashboard/hse/risk/SjaStepTemplatePicker";
import {
  emptySjaForm,
  riskApiError,
  SJA_WORK_TYPES,
} from "../../../../../components/dashboard/hse/risk/riskUtils";
import { sjaNotEditable } from "../../../../../components/dashboard/hse/risk/sjaApprovalUtils";
import { createSja, fetchSja, updateSja } from "../../../../../api/hse/risk";

const inputClass =
  "mt-2 w-full rounded-[14px] border-[1.5px] border-slate-200 bg-white px-4 py-3 text-[15px] outline-none focus:border-[#1463C7]";

export const HseKsSjaEdit = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [saveError, setSaveError] = useState("");
  const [uncertainSave, setUncertainSave] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState(emptySjaForm());

  useEffect(() => {
    if (isNew) return undefined;
    let cancelled = false;
    fetchSja(id)
      .then((record) => {
        if (cancelled) return;
        if (sjaNotEditable(record)) {
          toast.error(t("hse.sja.not_editable"));
          navigate(`/dashboard/hse-ks/risk/sja/${id}`);
          return;
        }
        setForm(record);
      })
      .catch((err) => {
        if (!cancelled) setError(riskApiError(err) || t("hse.risk.sja.load_failed"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, isNew, navigate, t]);

  const setField = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const updateStep = (index, key, value) => {
    setForm((current) => ({
      ...current,
      steps: current.steps.map((step, stepIndex) =>
        stepIndex === index ? { ...step, [key]: value } : step
      ),
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (savingRef.current || uncertainSave) return;
    const errors = validateSjaForm(form);
    setFieldErrors(errors);
    setSaveError("");
    if (Object.keys(errors).length) {
      setSaveError(t("hse.sja.fields.fix_errors"));
      return;
    }
    savingRef.current = true;
    setSaving(true);
    try {
      const saved = isNew ? await createSja(form) : await updateSja(id, form);
      if (!saved?.id) throw new Error(t("hse.sja.fields.save_unknown"));
      toast.success(isNew ? t("hse.risk.sja.created") : t("hse.risk.sja.updated"));
      navigate(`/dashboard/hse-ks/risk/sja/${saved.id || id}`);
    } catch (err) {
      const normalized = normalizeError(err);
      const unknown = !err?.response || err.response.status >= 500;
      setUncertainSave(unknown);
      setSaveError(unknown ? t("hse.sja.fields.save_unknown") : normalized.safeMessage || t("hse.risk.save_failed"));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <HsePageChrome
      title={isNew ? t("hse.risk.sja.new") : t("hse.risk.sja.edit")}
      subtitle={t("hse.risk.sja.form_subtitle")}
      backTo={id ? `/dashboard/hse-ks/risk/sja/${id}` : "/dashboard/hse-ks/risk/sja"}
    >
      <HsePageBody narrow>
        <div className="rounded-[18px] border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          {loading ? (
            <ResourceLoading />
          ) : error ? (
            <ResourceError errors={[error]} />
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              {saveError && <p role="alert" className="mb-4 rounded-xl bg-rose-50 p-3 text-sm text-rose-800">{saveError}</p>}
              <fieldset disabled={saving} className="min-w-0 space-y-5">
              <label className="block">
                <HseFieldInfoLabel
                  label={`${t("hse.risk.fields.title")} *`}
                  infoTitle="hse.risk.info.sja.title"
                  infoBody="hse.risk.info.sja.body"
                />
                <input
                  className={inputClass}
                  aria-invalid={!!fieldErrors.title}
                  aria-describedby={fieldErrors.title ? "sja-title-error" : undefined}
                  value={form.title}
                  onChange={(event) => setField("title", event.target.value)}
                />
                {fieldErrors.title && <span id="sja-title-error" className="text-sm text-rose-700">{t(fieldErrors.title)}</span>}
              </label>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className="text-[13px] font-bold text-slate-700">{t("hse.risk.sja.work_type")}</span>
                  <select
                    className={inputClass}
                    value={form.work_type}
                    onChange={(event) => setField("work_type", event.target.value)}
                  >
                    {SJA_WORK_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {t(`hse.risk.sja.work_type.${type}`)}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="text-[13px] font-bold text-slate-700">{t("hse.risk.sja.location")}</span>
                  <input
                    className={inputClass}
                    value={form.work_location}
                    onChange={(event) => setField("work_location", event.target.value)}
                  />
                </label>
              </div>
              <label className="block">
                <span className="text-[13px] font-bold text-slate-700">{t("hse.risk.fields.description")}</span>
                <textarea
                  rows={3}
                  className={inputClass}
                  value={form.description}
                  onChange={(event) => setField("description", event.target.value)}
                />
              </label>
              <div>
                <span className="text-[13px] font-bold text-slate-700">{t("hse.risk.fields.status")}</span>
                <p className="mt-2 text-sm font-semibold text-slate-800">
                  {t(`hse.risk.sja.status.${form.status || "draft"}`)}
                </p>
                <p className="mt-1 text-xs text-slate-500">{t("hse.sja.status_follows_approval")}</p>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-extrabold text-slate-900">{t("hse.risk.sja.steps")}</h3>
                  <button
                    type="button"
                    onClick={() =>
                      setForm((current) => ({
                        ...current,
                        steps: [
                          ...current.steps,
                          { step_number: nextSjaStepNumber(current.steps), description: "", hazards: [emptySjaHazard()] },
                        ],
                      }))
                    }
                    className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold"
                  >
                    <PlusIcon className="h-4 w-4" />
                    {t("hse.risk.sja.add_step")}
                  </button>
                </div>
                <SjaStepTemplatePicker
                  disabled={saving || uncertainSave}
                  onApply={(updater) => setForm((current) => updater(current))}
                />
                {form.steps.map((step, index) => !step._destroy && (
                  <div key={step.id ? `step-${step.id}` : `new-${index}`} className="rounded-[14px] border border-slate-200 p-4">
                    <div className="mb-2 flex items-center justify-between">
                      <p className="text-xs font-semibold uppercase text-slate-500">
                        {t("hse.risk.sja.step_n", { n: step.step_number })}
                      </p>
                      {form.steps.filter((row) => !row._destroy).length > 1 ? (
                        <button
                          type="button"
                          onClick={() =>
                            setForm((current) => ({
                              ...current,
                              steps: removeSjaRow(current.steps, index),
                            }))
                          }
                          aria-label={t("hse.sja.fields.remove_step")}
                          className="text-slate-400 hover:text-rose-600"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      ) : null}
                    </div>
                    <textarea
                      rows={2}
                      className={inputClass}
                      placeholder={t("hse.risk.sja.step_placeholder")}
                      aria-label={t("hse.risk.sja.step_placeholder")}
                      aria-invalid={!!fieldErrors[`steps.${index}.description`]}
                      aria-describedby={fieldErrors[`steps.${index}.description`] ? `step-${index}-error` : undefined}
                      value={step.description}
                      onChange={(event) => updateStep(index, "description", event.target.value)}
                    />
                    {fieldErrors[`steps.${index}.description`] && <p id={`step-${index}-error`} className="text-sm text-rose-700">{t(fieldErrors[`steps.${index}.description`])}</p>}
                    {step.hazards.map((hazard, hazardIndex) => !hazard._destroy && (
                      <SjaHazardFields key={hazard.id || `new-${hazardIndex}`} hazard={hazard}
                        path={`steps.${index}.hazards.${hazardIndex}`} errors={fieldErrors} inputClass={inputClass}
                        onChange={(key, value) => updateStep(index, "hazards", step.hazards.map((row, rowIndex) => rowIndex === hazardIndex ? { ...row, [key]: value } : row))}
                        onRemove={() => updateStep(index, "hazards", removeSjaRow(step.hazards, hazardIndex))} />
                    ))}
                    <button type="button" className="mt-3 text-sm font-semibold text-blue-700"
                      onClick={() => updateStep(index, "hazards", [...step.hazards, emptySjaHazard()])}>
                      {t("hse.sja.fields.add_hazard")}
                    </button>
                  </div>
                ))}
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() =>
                    navigate(isNew ? "/dashboard/hse-ks/risk/sja" : `/dashboard/hse-ks/risk/sja/${id}`)
                  }
                  className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold"
                >
                  {t("hse.risk.cancel")}
                </button>
                <button
                  type="submit"
                  disabled={saving || uncertainSave}
                  className="rounded-full bg-[#1463C7] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                >
                  {saving ? t("hse.risk.saving") : t("hse.risk.save")}
                </button>
              </div>
              </fieldset>
            </form>
          )}
        </div>
      </HsePageBody>
    </HsePageChrome>
  );
};
